/**
 * ShelfLabel.jsx
 * Renders a print-friendly shelf label for a SKU and exposes a `printShelfLabel(sku, company?)` helper
 * that opens a new browser window, writes a self-contained HTML label, and fires `window.print()`.
 *
 * Label fits a standard 62mm × 40mm thermal label (Brother QL / Zebra 2x1in) and scales down
 * gracefully on A4 when no label printer is attached. Uses JsBarcode to render the CODE128 barcode
 * as inline SVG so no external assets are required by the print window.
 */
import JsBarcode from 'jsbarcode';

/**
 * Returns the raw SVG markup for a barcode, or null if the value is too short/empty.
 */
function buildBarcodeSvg(value, width = 2, height = 70) {
  if (!value || value.length < 4) return null;
  try {
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    JsBarcode(svg, String(value), {
      format: 'CODE128',
      width,
      height,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return new XMLSerializer().serializeToString(svg);
  } catch {
    return null;
  }
}

/**
 * Opens a new window with one or more labels and triggers print. Safe to call from any handler.
 * @param {object|object[]} skuOrSkus - one SKU or an array of SKUs
 * @param {object} [opts] - { companyName, onDone }
 */
export function printShelfLabel(skuOrSkus, opts = {}) {
  const { companyName = '', onDone } = opts;
  const skus = Array.isArray(skuOrSkus) ? skuOrSkus.filter(Boolean) : (skuOrSkus ? [skuOrSkus] : []);
  if (skus.length === 0) return;

  const safeCompany = String(companyName || '').replace(/</g, '&lt;');

  const labelsHtml = skus.map((sku) => {
    const barcodeSvg = buildBarcodeSvg(sku.barcode) ||
      `<div style="font:11px monospace;color:#666;text-align:center;padding:18px 0;">No barcode</div>`;
    const safeName = String(sku.name || '').replace(/</g, '&lt;');
    return `
  <div class="page">
    <div class="company">${safeCompany || 'Stockaris WMS'}</div>
    <div class="sku-code">${sku.sku_code || ''}</div>
    <div class="name" title="${safeName}">${safeName}</div>
    <div class="barcode-box">${barcodeSvg}</div>
    <div class="footer">
      <span class="bar-text">${sku.barcode || ''}</span>
      <span class="dims">${(sku.weight_kg || 0)}kg · ${(sku.volume_m3 || 0)}m³</span>
    </div>
  </div>`;
  }).join('\n');

  const titleSuffix = skus.length === 1 ? skus[0].sku_code : `${skus.length} labels`;

  const html = `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<title>${titleSuffix} · Shelf labels</title>
<style>
  @page { size: 62mm 40mm; margin: 0; }
  @media print {
    html, body { margin: 0; padding: 0; background: #fff; }
    .toolbar { display: none !important; }
    .sheet { padding: 0 !important; gap: 0 !important; max-width: none !important; }
    .page { box-shadow: none !important; page-break-inside: avoid; break-inside: avoid; }
    /* Force one label per physical page on label printers */
    .page + .page { page-break-before: always; }
  }
  html, body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: #f5f5f5;
    color: #000;
  }
  .toolbar {
    position: fixed; top: 0; left: 0; right: 0;
    background: #fff; border-bottom: 1px solid #ddd;
    padding: 12px 20px; display: flex; gap: 8px; justify-content: center; align-items: center;
    z-index: 10;
  }
  .toolbar .count { font-size: 12px; color: #666; margin-right: 8px; }
  .toolbar button {
    padding: 8px 18px; border: 1px solid #000; background: #000; color: #fff;
    font: 12px/1.2 -apple-system, sans-serif; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.08em; cursor: pointer; border-radius: 3px;
  }
  .toolbar button.secondary { background: #fff; color: #000; }
  .sheet {
    padding: 80px 20px 20px;
    display: grid; grid-template-columns: repeat(auto-fill, minmax(62mm, 1fr));
    gap: 8mm; max-width: 1200px; margin: 0 auto;
  }
  .page {
    background: #fff;
    width: 62mm; height: 40mm;
    padding: 3mm 4mm;
    box-sizing: border-box;
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    overflow: hidden;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
  }
  .company {
    font-size: 7px; letter-spacing: 0.12em; text-transform: uppercase;
    color: #666; font-weight: 600;
  }
  .sku-code {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 14px; font-weight: 700; letter-spacing: 0.02em;
    line-height: 1.1; margin-top: 1mm; color: #000;
  }
  .name {
    font-size: 9px; font-weight: 500; color: #333;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    margin-top: 0.5mm;
  }
  .barcode-box {
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; margin: 1mm 0;
  }
  .barcode-box svg { max-width: 100%; max-height: 100%; }
  .footer {
    display: flex; justify-content: space-between; align-items: flex-end;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 7px; color: #555;
  }
  .footer .bar-text { letter-spacing: 0.05em; }
  .footer .dims { text-align: right; line-height: 1.2; }
</style>
</head><body>
<div class="toolbar">
  <span class="count" data-testid="label-count">${skus.length} label${skus.length === 1 ? '' : 's'}</span>
  <button onclick="window.print()" data-testid="label-print-btn">Print</button>
  <button class="secondary" onclick="window.close()">Close</button>
</div>
<div class="sheet">${labelsHtml}</div>
<script>
  // Give the browser a tick to paint before opening the print dialog
  window.addEventListener('load', function () {
    setTimeout(function () { window.focus(); window.print(); }, 300);
  });
</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=600');
  if (!w) {
    alert('Popup blocked — please allow popups to print shelf labels.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  if (onDone) onDone();
}

/**
 * Optional React wrapper — renders a small preview of the label. Rarely used directly;
 * the primary API is the `printShelfLabel()` function above.
 */
export default function ShelfLabel({ sku, companyName = '' }) {
  if (!sku) return null;
  const svg = buildBarcodeSvg(sku.barcode);
  return (
    <div
      style={{
        width: '62mm', height: '40mm', background: '#fff', color: '#000',
        padding: '3mm 4mm', boxSizing: 'border-box', border: '1px solid #e5e7eb',
        display: 'grid', gridTemplateRows: 'auto auto 1fr auto', overflow: 'hidden',
      }}
      data-testid="shelf-label-preview"
    >
      <div style={{ fontSize: 7, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>
        {companyName || 'Stockaris WMS'}
      </div>
      <div style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: 14, fontWeight: 700, marginTop: '1mm' }}>
        {sku.sku_code}
      </div>
      <div style={{ fontSize: 9, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '0.5mm' }}>
        {sku.name}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', margin: '1mm 0' }}
           dangerouslySetInnerHTML={{ __html: svg || '<span style="font:11px monospace;color:#666">No barcode</span>' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'SF Mono, monospace', fontSize: 7, color: '#555' }}>
        <span>{sku.barcode}</span>
        <span>{(sku.weight_kg || 0)}kg · {(sku.volume_m3 || 0)}m³</span>
      </div>
    </div>
  );
}
